import * as React from "react";
import { usePortalSeo } from "../config";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@alight/contracts";
import { Button, Card, CardContent, Container, PageHeader, Section } from "@alight/ui";

/**
 * Magic-link landing page: /auth/verify?token=…&redirect=…
 *
 * The emailed link points HERE (a page, not the API) and the page POSTS the
 * token — so a mail scanner prefetching the URL can't burn the one-time token.
 * On success the API sets the session cookie and we forward to the (server-
 * sanitised) redirect; on failure the visitor can request a fresh link.
 */

const api = apiClient("");

export function VerifyPage() {
  usePortalSeo("Sign In");
  const { token, redirect } = useSearch({ strict: false }) as {
    token: string;
    redirect: string;
  };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [state, setState] = React.useState<"verifying" | "ok" | "failed">("verifying");
  const [message, setMessage] = React.useState<string | null>(null);
  const ran = React.useRef(false); // StrictMode double-mount must not burn the token twice

  React.useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) {
      setState("failed");
      setMessage("This link is missing its sign-in code — request a fresh one.");
      return;
    }
    void api
      .verifyMagicToken(token)
      .then((r) => {
        setState("ok");
        // Small pause so the "verified" state is visible, then continue.
        // The nav (and every auth-aware page) caches ["auth","me"] — without
        // this, the client-side navigate keeps rendering the signed-out state
        // (no My account / Admin tabs) until a hard refresh.
        void queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
        void queryClient.invalidateQueries({ queryKey: ["my"] });
        setTimeout(() => void navigate({ to: r.redirect || redirect || "/" }), 600);
      })
      .catch((err: unknown) => {
        setState("failed");
        setMessage(
          err instanceof Error && /expired|already used/i.test(err.message)
            ? "This sign-in link has expired or was already used."
            : "We couldn't verify this sign-in link.",
        );
      });
  }, [token, redirect, navigate, queryClient]);

  return (
    <>
      <PageHeader eyebrow="Sign in" title="Verifying your email" description="One moment…" />
      <Section tone="paper" spacing="lg">
        <Container width="narrow">
          <Card>
            <CardContent className="p-6 text-center sm:p-8">
              {state === "verifying" && <p className="text-sm text-muted">Checking your sign-in link…</p>}
              {state === "ok" && (
                <p className="text-sm font-semibold text-primary">✓ Email verified — taking you back…</p>
              )}
              {state === "failed" && (
                <div className="space-y-4">
                  <p role="alert" className="text-sm font-medium text-accent">
                    {message}
                  </p>
                  <p className="text-sm text-muted">
                    Links work once and expire in 15 minutes — head back and request a fresh one.
                  </p>
                  <Button
                    onClick={() => void navigate({ to: redirect && redirect.startsWith("/") ? redirect : "/" })}
                  >
                    Continue
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </Container>
      </Section>
    </>
  );
}
